const configuredApiBase = (import.meta.env?.VITE_BEAUTINOW_SEARCH_API_BASE || "").replace(/\/$/, "");

export type SessionBootstrapRuntime = {
  search: string;
  href: string;
  apiBase: string;
  fetcher: typeof fetch;
  replaceUrl: (url: string) => void;
};

export type SessionBootstrapResult = { mode: "browser" | "miniapp" };

export function isMiniProgramSession(search: string): boolean {
  return new URLSearchParams(search).get("channel") === "wechat-mini-program";
}

export function isGuestMiniProgramSession(search: string): boolean {
  const parameters = new URLSearchParams(search);
  return parameters.get("channel") === "wechat-mini-program" && parameters.get("guest") === "1";
}

function cleanTicketUrl(href: string): string {
  const url = new URL(href);
  url.searchParams.delete("ticket");
  return `${url.pathname}${url.search}${url.hash}`;
}

function browserRuntime(): SessionBootstrapRuntime {
  return {
    search: window.location.search,
    href: window.location.href,
    apiBase: configuredApiBase,
    fetcher: window.fetch.bind(window),
    replaceUrl: (url) => window.history.replaceState(null, "", url),
  };
}

export async function bootstrapSession(
  runtime: SessionBootstrapRuntime = browserRuntime(),
): Promise<SessionBootstrapResult> {
  if (!isMiniProgramSession(runtime.search)) return { mode: "browser" };
  if (!runtime.apiBase) throw new Error("Search 登录服务尚未配置");

  const ticket = new URLSearchParams(runtime.search).get("ticket") || "";
  const hasTicket = ticket.length > 0;
  if (hasTicket && !/^[A-Za-z0-9_-]{32,256}$/.test(ticket)) {
    throw new Error("微信登录凭证无效或已过期，请返回小程序重新进入");
  }
  if (!hasTicket && isGuestMiniProgramSession(runtime.search)) return { mode: "miniapp" };

  const response = hasTicket
    ? await runtime.fetcher(`${runtime.apiBase}/auth/webview-exchange`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ticket }),
      })
    : await runtime.fetcher(`${runtime.apiBase}/auth/session`, {
        method: "GET",
        credentials: "include",
      });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.ok !== true || (!hasTicket && payload?.authenticated !== true)) {
    const fallbackMessage = hasTicket
      ? "微信登录凭证交换失败，请返回小程序重试"
      : "微信登录状态已过期，请返回小程序重新进入";
    throw new Error(typeof payload?.detail === "string" ? payload.detail : fallbackMessage);
  }

  if (hasTicket) runtime.replaceUrl(cleanTicketUrl(runtime.href));
  return { mode: "miniapp" };
}
